import {
  Stack,
  Typography,
  Grid,
  Paper,
  Box,
  Divider,
} from '@mui/material'
import { 
  Assignment,
  Timeline,
  Cake,
} from '@mui/icons-material'

const EntryDetailsTab = ({ entry }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString()
  }

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'Not recorded'
    const date = new Date(dateString)
    return date.toLocaleDateString()
  }

  const calculateDetailedAge = (hatchDate) => {
    if (!hatchDate) return null
    const now = new Date()
    const hatched = new Date(hatchDate)
    const diffInMs = now - hatched
    const ageInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
    
    if (ageInDays < 0) return 'Future date'
    if (ageInDays === 0) return 'Hatched today'
    if (ageInDays === 1) return '1 day old'
    if (ageInDays < 14) return `${ageInDays} days old`
    
    const ageInWeeks = Math.floor(ageInDays / 7)
    if (ageInWeeks < 12) return `${ageInWeeks} week${ageInWeeks !== 1 ? 's' : ''} old`
    
    const ageInMonths = Math.floor(ageInDays / 30.44)
    if (ageInMonths < 24) return `${ageInMonths} month${ageInMonths !== 1 ? 's' : ''} old`
    
    const ageInYears = Math.floor(ageInDays / 365.25)
    const remainingMonths = Math.floor((ageInDays - (ageInYears * 365.25)) / 30.44)
    
    if (remainingMonths > 0) {
      return `${ageInYears} year${ageInYears !== 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''} old`
    }
    return `${ageInYears} year${ageInYears !== 1 ? 's' : ''} old`
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h6" gutterBottom>
        Detailed Information
      </Typography>
      
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment color="primary" />
              Entry Metadata
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Entry ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {entry?.entryId || 'Not available'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created
                </Typography>
                <Typography variant="body1">
                  {formatDate(entry?.createdAt)}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last Modified
                </Typography>
                <Typography variant="body1">
                  {formatDate(entry?.updatedAt)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Timeline color="primary" />
              Quick Stats
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Notes Count
                </Typography>
                <Typography variant="h6">
                  {entry?.notes?.archived?.length || 0} archived
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Weight Records
                </Typography>
                <Typography variant="h6">
                  {entry?.weights?.length || 0} entries
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Characteristics
                </Typography>
                <Typography variant="h6">
                  {entry?.characteristics?.length || 0} defined
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Cake color="primary" />
              Age Information
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Date of Birth (Hatch Date)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatDateOnly(entry?.hatchDate)}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Age
                </Typography>
                <Typography variant="h6" color="primary">
                  {calculateDetailedAge(entry?.hatchDate) || 'Unknown age'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {entry?.notes?.active && (
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Active Notes
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {entry.notes.active}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Stack>
  )
}

export default EntryDetailsTab